import React, { useEffect, useState } from "react";
import { Modal, Typography, Form, Input, Checkbox, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import PrimaryButton from "../Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { formRules } from "../../constant";

const { Title, Text } = Typography;

const AddEditBrochureModal = ({ isOpen, closeModal, data, onConfirm, loading = false }) => {
  const [form] = Form.useForm();
  const { showPopup } = useGlobalModal();

  const [isPdfOriginal, setIsPdfOriginal] = useState(false);
  const [isImageOriginal, setIsImageOriginal] = useState(false);

  const isEditMode = !!data;
  const title = isEditMode ? "Edit Brochure" : "Add Brochure";
  const btnText = isEditMode ? "Edit" : "Add";

  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        const hasPdf = !!data.brochure_pdf;
        const hasImage = !!data.brochure_picture;

        setIsPdfOriginal(hasPdf);
        setIsImageOriginal(hasImage);

        // 1. Format the PDF file for Ant Design Upload
        const pdfList = hasPdf
          ? [
              {
                uid: "-1",
                name: data.brochure_pdf,
                status: "done",
                url: data.brochure_pdf_url,
              },
            ]
          : [];

        // 2. Format the Image file for Ant Design Upload
        const imageList = hasImage
          ? [
              {
                uid: "-2",
                name: data.brochure_picture,
                status: "done",
                url: data.brochure_picture_url,
              },
            ]
          : [];

        // 3. Inject ALL fields into the form
        form.setFieldsValue({
          name: data.name || data.brochure_name || "",
          is_active: data.status === "Active" || data.status === 1 || data.status === true,
          pdf_file: pdfList,
          image_file: imageList,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ is_active: true });
        setIsPdfOriginal(false);
        setIsImageOriginal(false);
      }
    }
  }, [isOpen, data, form]);

  const normPdfFile = (e) => {
    let fileList = Array.isArray(e) ? e : e?.fileList;

    // If edit mode and list is cleared, restore original
    if (isEditMode && (!fileList || fileList.length === 0) && data?.brochure_pdf) {
      setIsPdfOriginal(true);
      return [
        {
          uid: "-1",
          name: data.brochure_pdf,
          status: "done",
          url: data.brochure_pdf_url,
        },
      ];
    }

    setIsPdfOriginal(fileList?.[0]?.uid === "-1");
    return fileList;
  };

  const normImageFile = (e) => {
    let fileList = Array.isArray(e) ? e : e?.fileList;

    // If edit mode and list is cleared, restore original
    if (isEditMode && (!fileList || fileList.length === 0) && data?.brochure_picture) {
      setIsImageOriginal(true);
      return [
        {
          uid: "-2",
          name: data.brochure_picture,
          status: "done",
          url: data.brochure_picture_url,
        },
      ];
    }

    setIsImageOriginal(fileList?.[0]?.uid === "-2");
    return fileList;
  };

  const handleFinish = async (values) => {
    const pdfFile = values.pdf_file?.[0]?.originFileObj;
    const imageFile = values.image_file?.[0]?.originFileObj;

    const payload = {
      name: values.name,
      status: values.is_active ? "Active" : "Disabled",
      pdf_file: pdfFile,
      image_file: imageFile,
    };

    if (typeof onConfirm === "function") {
      await onConfirm(payload, data);
    }
  };

  return (
    <Modal
      open={!!isOpen}
      centered
      title={
        <Title level={2} className="font-abhaya !mb-0">
          {title}
        </Title>
      }
      onCancel={closeModal}
      destroyOnHidden
      width={450}
      footer={null}
      className="form-css-5"
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} className="mt-4">
        <Form.Item label="Brochure Name" name="name" rules={formRules.required}>
          <Input placeholder="Enter brochure name" className="h-10" autoComplete="off" />
        </Form.Item>

        <Form.Item name="is_active" valuePropName="checked" className="mb-6 ms-1">
          <Checkbox className="font-medium text-gray-700">Active</Checkbox>
        </Form.Item>

        <div className="flex justify-between gap-4 w-full">
          <Form.Item
            label="Upload PDF"
            name="pdf_file"
            valuePropName="fileList"
            getValueFromEvent={normPdfFile}
            rules={formRules.required}
            className="flex-1 w-full min-w-0"
          >
            <Upload
              accept=".pdf"
              maxCount={1}
              showUploadList={{ showRemoveIcon: !isPdfOriginal }}
              className="w-full block [&_.ant-upload]:w-full [&_.ant-upload-select]:w-full [&_.ant-upload-list-item-name]:max-w-[160px] [&_.ant-upload-list-item-name]:truncate [&_.ant-upload-list-item-name]:inline-block"
              beforeUpload={(file) => {
                const isPdf =
                  file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
                if (!isPdf) {
                  showPopup({
                    title: null,
                    content: (
                      <span className="custom-modal-title">Please upload only PDF files.</span>
                    ),
                    onOk: () => console.log("Confirmed!"),
                    className: "custom-success-modal",
                  });
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
            >
              <Button className="h-10 w-full" icon={<UploadOutlined />}>
                {isEditMode ? "Change PDF" : "Select PDF"}
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="Upload Cover Image"
            name="image_file"
            valuePropName="fileList"
            getValueFromEvent={normImageFile}
            rules={formRules.required}
            className="flex-1 w-full min-w-0"
          >
            <Upload
              accept=".jpg,.jpeg,.png"
              maxCount={1}
              showUploadList={{ showRemoveIcon: !isImageOriginal }}
              className="w-full block [&_.ant-upload]:w-full [&_.ant-upload-select]:w-full [&_.ant-upload-list-item-name]:max-w-[160px] [&_.ant-upload-list-item-name]:truncate [&_.ant-upload-list-item-name]:inline-block"
              beforeUpload={(file) => {
                const isImg =
                  file.name.toLowerCase().endsWith(".jpg") ||
                  file.name.toLowerCase().endsWith(".jpeg") ||
                  file.name.toLowerCase().endsWith(".png") ||
                  file.type.startsWith("image/");

                if (!isImg) {
                  showPopup({
                    title: null,
                    content: (
                      <span className="custom-modal-title">
                        Please upload only JPG or PNG files.
                      </span>
                    ),
                    onOk: () => console.log("Confirmed!"),
                    className: "custom-success-modal",
                  });
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
            >
              <Button className="h-10 w-full" icon={<UploadOutlined />}>
                {isEditMode ? "Change Image" : "Select Image"}
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="w-full flex justify-center mt-2">
          <PrimaryButton
            text={btnText}
            type="submit"
            htmlType="submit"
            loading={loading}
            className="me-[30px]"
          />
        </div>
      </Form>
    </Modal>
  );
};

export default AddEditBrochureModal;
